import React, { useEffect, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Button,
  Badge,
  Box
} from '@mui/material';
import { ExpandMore, Visibility, Edit, Delete } from '@mui/icons-material';
import fascicoloApi from '../../api/fascicoloApi';
import { AuthContext } from '../../auth/AuthContext';
import ProtectedAction from '../../components/ProtectedAction';
import { formatDate } from '../../utils/dateFormatter';
import FascicoloChat from '../../components/FascicoloChat';

export default function FascicoloDetailPageMobile() {
  const { id } = useParams();
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();

  const [contribuente, setContribuente] = useState({});
  const [documenti, setDocumenti] = useState([]);
  const [richiesteNonEvase, setRichiesteNonEvase] = useState([]);
  const [fascicoloId, setFascicoloId] = useState(null);

  useEffect(() => {
    const fetchFascicolo = async () => {
      try {
        const res = id
          ? await fascicoloApi.getFascicoloByContribuenteId(id)
          : await fascicoloApi.getMyFascicolo();
        setFascicoloId(res.data.id);
        setContribuente(res.data.contribuente);
        setDocumenti(res.data.documenti);
        fetchRichiesteNonEvase(res.data.id);
      } catch (err) {
        console.error(err);
      }
    };

    const fetchRichiesteNonEvase = async (fid) => {
      try {
        const res = await fascicoloApi.getRichiesteNonEvase(fid);
        setRichiesteNonEvase(res.data);
      } catch (err) {
        console.error(err);
      }
    };

    fetchFascicolo();
  }, [id]);

  const handleNuovoDocumento = () => {
    navigate(`/fascicolo/${fascicoloId}/documento/create`);
  };

  const handleDownload = async (documentoId, fileName) => {
    try {
      const response = await fascicoloApi.downloadDocumento(documentoId);
        const url = window.URL.createObjectURL(
                              new Blob([response.data], { type: 'application/pdf' })
        );
        window.open(url, '_blank');
    } catch (err) {
      console.error(err);
    }
  };

  const handleDelete = async (documentoId) => {
    try {
      await fascicoloApi.deleteDocumento(documentoId);
      setDocumenti(documenti.filter(d => d.id !== documentoId));
    } catch (err) {
      console.error(err);
    }
  };

  const handleCambiaStatoRichiesta = async (richiestaId, stato) => {
    try {
      await fascicoloApi.changeStatoRichiesta(richiestaId, stato);
      setRichiesteNonEvase(
        richiesteNonEvase.map(r => (r.id === richiestaId ? { ...r, stato } : r))
      );
    } catch (err) {
      console.error(err);
    }
  };

  const handleCreaDocumentoDaRichiesta = (richiestaId, tipo) => {
    navigate(`/fascicolo/${fascicoloId}/documento/create`, { state: { richiestaId, tipo } });
  };

  return (
    <Container sx={{ mt: 2 }}>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Fascicolo di {contribuente.nome} {contribuente.cognome}
      </Typography>

      {/* Documenti Fascicolo */}
      <Stack spacing={2} sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Documenti Fascicolo</Typography>
          <ProtectedAction user={user} contractState={user.statoContratto}>
            <Button variant="contained" onClick={handleNuovoDocumento}>
              Carica Documento
            </Button>
          </ProtectedAction>
        </Stack>

        {documenti.map(d => (
          <Accordion key={d.id}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography>{d.tipo} {d.descrizione}</Typography>
                {d.rateSaltate > 0 && <Badge color="error" badgeContent={d.rateSaltate} />}
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">{d.contribuente?.nome} {d.contribuente?.cognome}</Typography>
              <Typography variant="body2">Data creazione: {d.dataCreazione}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                <Button size="small" onClick={() => navigate(`/documenti/${d.id}`)} startIcon={<Visibility />}>
                  Visualizza
                </Button>
                <ProtectedAction user={user} contractState={user.statoContratto}>
                  <Button size="small" onClick={() => navigate(`/documenti/edit/${d.id}`)} startIcon={<Edit />}>
                    Modifica
                  </Button>
                  <Button size="small" color="error" onClick={() => handleDelete(d.id)} startIcon={<Delete />}>
                    Elimina
                  </Button>
                </ProtectedAction>
                {d.pdfPath && (
                  <Button size="small" onClick={() => handleDownload(d.id, d.pdfPath)}>
                    Apri PDF
                  </Button>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      {/* Richieste Documento */}
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Richieste Documento</Typography>
          <ProtectedAction user={user} roles={['ADMIN', 'STUDIO']} contractState={user.statoContratto}>
            <Button
              variant="contained"
              onClick={() => navigate(`/fascicolo/${fascicoloId}/richieste/create`)}
            >
              Nuova richiesta
            </Button>
          </ProtectedAction>
        </Stack>

        {richiesteNonEvase.map(r => (
          <Accordion key={r.id}>
            <AccordionSummary expandIcon={<ExpandMore />}>
              <Stack direction="row" spacing={1} sx={{ width: '100%', justifyContent: 'space-between' }}>
                <Typography>{r.tipoDocumento} - Stato: {r.stato}</Typography>
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <Typography variant="body2">Richiesto da: {r.richiestoDa?.nome} {r.richiestoDa?.cognome}</Typography>
              <Typography variant="body2">Data richiesta: {r.dataRichiesta}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: 'wrap' }}>
                {r.stato !== 'CARICATO' && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleCambiaStatoRichiesta(r.id, 'VALIDATO')}
                  >
                    Valida richiesta
                  </Button>
                )}
                {r.stato === 'RICHIESTO' && (
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={() => handleCreaDocumentoDaRichiesta(r.id, r.tipoDocumento)}
                  >
                    Crea Documento
                  </Button>
                )}
                {r.pdfPath && (
                  <Button size="small" onClick={() => handleDownload(r.id, r.pdfPath)}>
                    Apri PDF
                  </Button>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>
      {fascicoloId && contribuente.studio && user.chatisactive==true && (
        <FascicoloChat fascicoloId={fascicoloId} />
      )}
    </Container>
  );
}
